/**
 * Done (§5.2/§7.3/§7.4): the outcome, restated journey + attribution, the
 * customer send, and honest states everywhere — a nameless price check says
 * nothing was saved; 'New Quote' (net-locked) starts the next job through the
 * single reset.
 *
 * ONE deposit action (Vlad, 2026-07-19). The standalone 'Send deposit link'
 * button used to sit here as the primary, minting a link and opening Stripe
 * checkout in Helen's own tab. Both are gone: the deposit link is now minted
 * only by 'Send quote to customer' with its deposit toggle, which sends it to
 * the person who is actually paying. Helen is not that person, so nothing here
 * navigates her to a payment page.
 *
 * The `/deposit-link` route and `sendDepositLink` action deliberately REMAIN.
 * They hold the bug-e keyless-503 contract lock, and they are the seam for a
 * phone-booking fallback (operator forwards a link by hand) if that is ever
 * wanted. They simply have no button on this screen any more.
 *
 * ⚠️ T660 M3 — THE ANTICIPATED FALLBACK HAS LANDED, AND IT IS NOT ON THIS ROUTE.
 * "Payment link only" (in the shared delivery block's "Or copy it" group, on
 * this screen and on BookingDetail) is exactly the hand-forwarding seam the
 * paragraph above reserved. It rides M1's `POST /api/bookings/:id/payment-link`,
 * which REUSES an existing live link and refuses rather than falling through to
 * mint. It does NOT ride `/deposit-link`, which mints a new link and sweeps the
 * old one - pointing the new button at that route would deactivate a link the
 * customer may already be holding, which is the whole of the P0-4 defect. So:
 *  - `sendDepositLink` and `/deposit-link` still REMAIN, still unbuttoned, still
 *    holding the bug-e contract lock (quote-flow.spec.tsx). Do not delete them
 *    and do not re-target the new button at them.
 *  - the "Deposit sent" banner below stays DEAD on purpose. It is gated on
 *    `job.depositUrl`, which only `sendDepositLink` writes, and the new action
 *    deliberately writes nothing to the store: the URL is used and discarded, so
 *    it can never reach the DOM or a feedback screenshot. Reviving the banner
 *    would also put its own "Copy payment link" button on the same screen as
 *    "Payment link only", one being a substring of the other.
 */
import { useState } from 'preact/hooks';
import type { JobState } from '../store/state.js';
// ⭐ Board 2089 M3 (R-7) — the shared customer-phone read-back, also read by
// CustomerInfoBlock, so the two screens cannot render one number two ways.
import { customerPhoneRead } from '../store/selectors.js';
import { PRICE_ENTRY_KIND_BY_SERVICE, type ServiceId } from '../../shared/service-schema.js';
import type { Channel } from '../../shared/send-schema.js';
import { euro } from '../lib/format.js';
import { DEPOSIT_KEYLESS_COPY, suggestForPriceList, type CopyLinkOutcome, type CopyQuoteOutcome, type SendQuoteOutcome } from '../lib/actions.js';
import { Banner } from '../components/Banner.js';
import { SendQuote, sendQuoteHasPrimary } from '../components/SendQuote.js';

export type DoneProps = {
  job: JobState;
  operator: string;
  summary: string;
  /**
   * ⭐ T1749 M8 round 3 — the journey's EXTRAS, as their own caption line (see the
   * render, and `store/selectors.ts`'s `journeyExtras`). OPTIONAL and defaulting
   * to nothing, following the props below: every fixed-prop `<Done>` render in the
   * suite keeps exactly today's markup, and a caller that says nothing renders no
   * line rather than an empty one.
   */
  extras?: string;
  sendStatusLabel: (status: string) => string;
  /** G3 channel label (T549 M7) — so the send section can say which channel a
   *  delivery status belongs to, and name the picker's options. Optional because
   *  App.tsx always supplies it, and SendQuote falls back to the raw channel
   *  value exactly as the registry accessor does, so no existing Done render
   *  needs a prop it never used. */
  channelLabel?: (channel: string) => string;
  /**
   * T524 (C5) — can the server actually send on this channel? Passed straight
   * through to SendQuote, which uses it for the DEFAULT selection only.
   *
   * Done holds no registry (it takes `sendStatusLabel`/`channelLabel` as
   * functions for exactly that reason), so this is a pass-through rather than
   * something it can derive. Optional, like its two neighbours: App.tsx always
   * supplies it, and every fixed-prop <Done> render in the suite stays valid.
   */
  channelConfigured?: (channel: Channel) => boolean;
  onSendQuote: (includeDeposit: boolean, channel: Channel) => Promise<SendQuoteOutcome>;
  onNewQuote: () => void;
  /* ⛔ NO `onGenerateInvoice` (board 2764 M6, R-12). The "Generate invoice"
     button it fed opened the kiosk's own statement of runs, which is gone: the
     document a home receives is Stripe's, opened from the Invoices tab. */
  /**
   * T524 — fetch the customer-facing quote message for the clipboard.
   *
   * OPTIONAL: App.tsx always supplies it, and several suite renders build Done
   * with a fixed prop object, so a required member would break renders that
   * never used it. Its presence is ALSO part of the button's gate - a Done
   * rendered without a handler shows no button rather than a no-op one.
   *
   * The in-flight guard lives in App.tsx rather than here because Done holds no
   * store: `quotePending` is a store selector, and Helen must not copy a message
   * whose total differs from the one on the screen in front of her.
   */
  onCopyQuoteMessage?: () => Promise<CopyQuoteOutcome>;
  /**
   * T660 M3 (P0-3) — fetch this booking's live deposit URL for the clipboard.
   *
   * OPTIONAL for the same reason as onCopyQuoteMessage directly above: App.tsx
   * always supplies it, and the suite's fixed-prop <Done> renders stay valid
   * without a prop they never used. Its presence is also the button's gate, so
   * such a render shows no button rather than a no-op one.
   *
   * NO re-price guard travels with it, and that is a finding rather than an
   * oversight - App.tsx's wiring records the evidence.
   */
  onCopyPaymentLink?: () => Promise<CopyLinkOutcome>;
  /**
   * ⭐⭐ T1749 M8 (DS-20 d) — THE ONE WAY BACK, and the only one this screen had
   * was the shell's `Back`, which retraces the wizard she just came through.
   *
   * Helen's actual next move after recording a quote is the next PENDING one, so
   * the link opens Bookings on the `New` filter — the unquoted work. App.tsx owns
   * the hand-off (a `bookingsOpenOn` it hands the Bookings mount and clears), for
   * the same reason `nursingOpenOn` is not in the store: `navigate` moves between
   * SCREENS, and which filter a screen opens on is that screen's own affair and
   * survives exactly one navigation.
   *
   * OPTIONAL, following the four props above and for the same reason: App.tsx
   * always supplies it, and every fixed-prop <Done> render in the suite stays
   * valid. Its presence is also the control's gate — a Done wired without it
   * shows no link rather than a no-op one.
   */
  onBackToBookings?: () => void;
};

/** SEC-9: only ever open https: URLs. */
export function openHttps(url: string): void {
  if (url.startsWith('https:')) window.open(url, '_blank', 'noopener');
}

/**
 * Put an https: link on the clipboard. Same SEC-9 discipline as openHttps.
 *
 * Redundancy on the money path (Vlad, 2026-07-19). ECMB takes phone bookings,
 * not walk-ins, so a deposit always has a phone to text -- but SMS is a single
 * transport with a known intermittent failure on Irish numbers. Copying lets
 * Helen paste the same link into WhatsApp on her desktop and get the customer
 * paid anyway. It is a clipboard utility, deliberately NOT a second send
 * action, and it never navigates her to a payment page.
 *
 * Returns false rather than throwing where the Clipboard API is unavailable
 * (it needs a secure context), so the button can tell the truth instead of
 * silently doing nothing.
 */
export async function copyHttps(url: string): Promise<boolean> {
  if (!url.startsWith('https:')) return false;
  try {
    await navigator.clipboard.writeText(url);
    return true;
  } catch {
    return false;
  }
}

/** ⭐ board 2812 M5 (fix round 1, W7): where a quote saved for later waits (R-11: listed
 *  for Vicky). `walkIn` is the sentence this screen always said. */
export const PARKED_WORDS = {
  fromEnquiry: 'Saved with no price. It is waiting in Web enquiries, on To do: press Resume quote on its card when you\'re ready to finish the quote.',
  walkIn: 'Saved with no price. Pick it up from Bookings when you\'re ready to finish the quote.',
} as const;

export function Done(props: DoneProps) {
  const { job } = props;
  const saved = job.doneOutcome === 'saved';
  // T409: parked = saved for later WITHOUT a quote. No total is headlined
  // (nothing was recorded — honest provenance), and none of the saved-only
  // send/deposit surfaces render (they gate on `saved`).
  const parked = job.doneOutcome === 'parked';
  const total = job.finalTotalCents ?? job.quote?.totalCents ?? null;
  const depositPaid = job.paymentStatus === 'deposit_paid';
  // Board 2089 M3/M5: the server-of-record PAIR, never `known ? phone : null` -
  // that collapse spelled "landed, no number" as "not landed yet" and re-opened
  // T651 below (see `customerPhoneRead`). Landed, the record decides, null
  // included; not landed, the typed pair is parsed.
  const donePhone = customerPhoneRead(job, { known: job.serverPhoneKnown, e164: job.serverPhone });
  /* ⛔ `accountLinked` (T651 M7: an account booking's typed number is the home's,
     so no tel: link) WAS HERE and went with the nursing tile at board 2814 M6
     (DD-8): the calculator never makes an account booking now. */

  // T660 M6 (R1) — the send block's mount condition, named ONCE, because the
  // variant of 'New Quote' at the foot of this screen now depends on it and two
  // hand-written copies of one gate is how they drift apart.
  const sendBlock = saved && job.bookingId !== null && total != null && total > 0;
  /**
   * Is the SEND the gold on this screen right now?
   *
   * P0-22's intent is "the send action is the visually dominant control on
   * Done", which is only meaningful when a send action is actually there.
   * Making 'New Quote' unconditionally secondary left THREE states with no
   * primary at all — a parked save and a price check never mount the block, and
   * its already-sent arm is `btn--quiet` by M4's design — plus a fourth the
   * mount condition alone cannot see: a saved, quoted booking with no phone and
   * no email mounts the block and renders no send button inside it.
   *
   * So the question is asked of the block itself (`sendQuoteHasPrimary`, beside
   * the button whose class it describes) rather than restated here. The screen
   * rule this buys: EXACTLY ONE `btn--primary` in every Done state.
   */
  /**
   * ⭐⭐ T1749 M8 round 2 (security Med2) — THIS SCREEN'S OWN WITNESS OF THE 2xx.
   *
   * `job.latestSend` is written by `refreshBookingDetail`, the read that runs
   * AFTER the send and can fail — and this screen's whole one-primary rule hangs
   * off it. So a send that succeeded with a failed refresh left the gold on
   * 'Send quote to customer' (one tap from a second deposit link) while the send
   * block itself had already moved on. The host watches the handler's own outcome
   * and hands it to `sendQuoteHasPrimary`, so the block and the screen answer
   * "is the send still next" from the same fact.
   * ⚠️ Local state, not the store: it describes THIS mount's send, and
   * `resetForNewQuote` is what ends the job.
   */
  const [sentOk, setSentOk] = useState(false);
  const primaryIsSend = sendBlock && sendQuoteHasPrimary({
    serverPhoneKnown: job.serverPhoneKnown,
    serverPhone: job.serverPhone,
    serverEmail: job.customerEmail,
    latestSend: job.latestSend,
    sentOk,
  });

  // T387 M6 — the one-tap "suggest for the price list" flag: a QUEUE input,
  // never a rates write. Rendered only for a SAVED job that carries a
  // suggestable key: the venue on a manual-priced service, or an off-list
  // place Helen hand-quoted. Nothing suggestable → no button (never a wall).
  const [suggested, setSuggested] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');
  const [copied, setCopied] = useState<'idle' | 'copied' | 'failed'>('idle');
  const suggestable = ((): { service: string; venue?: string; place?: string } | null => {
    if (!saved || job.bookingId == null || !job.service) return null;
    const kind = PRICE_ENTRY_KIND_BY_SERVICE[job.service as ServiceId];
    // Don't offer to suggest a venue that ALREADY has a standing rate: the
    // engine returns status 'priced' iff a rates row exists for exactly this
    // service|venue|band (per-band; an override keeps 'priced'), so a priced
    // venue is already on the list — suggesting it only yields a queue card
    // that 409s on promote. A missing band still reads 'manual' → still
    // suggestable, which is correct (Helen wants to add that band's price).
    if (kind === 'venue' && job.venue && job.venue.trim() && job.quote?.status !== 'priced') {
      return { service: job.service, venue: job.venue.trim() };
    }
    if (job.pickupOffGazetteer && job.locationLabel && job.locationLabel.trim()) {
      return { service: job.service, place: job.locationLabel.trim() };
    }
    if (job.dropoffOffGazetteer && job.toLocationLabel && job.toLocationLabel.trim()) {
      return { service: job.service, place: job.toLocationLabel.trim() };
    }
    return null;
  })();

  const sendSuggestion = async () => {
    if (suggestable == null || job.bookingId == null || suggested === 'sending') return;
    setSuggested('sending'); // double-tap guard: one tap, one row
    const ok = await suggestForPriceList(suggestable, job.bookingId);
    setSuggested(ok ? 'sent' : 'failed');
  };

  return (
    <div class="screen-pane">
      <h1 class="screen-title" tabIndex={-1}>
        {saved ? 'Quote recorded' : parked ? 'Saved for later' : 'Price check'}
      </h1>

      {/* ⭐⭐ T1749 M8 round 3 (ux round 2's rung 2) — THE EXTRAS ARE A CAPTION LINE
          UNDER THE JOURNEY, not the tail of its sentence. With five requirements
          toggled the sentence wrapped to two lines at 1024 and pushed this
          screen's gold primary to 703.3 against the ~700 plane; as a tight
          caption line the same five facts cost one line and read as the list they
          are. ⛔ ONE SOURCE: `journeyExtras` is what `journeySummary` itself
          composes, so the sentence on Quote and this pair can never disagree
          about which extras a job carries. Quote and the lead strip are
          untouched. */}
      {props.summary && <p class="journey-summary">{props.summary}</p>}
      {props.extras && <p class="journey-extras">{props.extras}</p>}


      {/* T549 M4: provenance survives the Quote screen. This is the screen the
          quote is SENT from, so it was the last chance to see that the number
          about to reach a customer is a distance estimate rather than a list
          price — and the label collapsed to 'Total' regardless. Same chip and
          same word as the Quote headline, so one price reads one way throughout.

          The chip is gated on `!wasHandQuoted`, which is the same condition that
          picks 'Agreed price': the headline number is `finalTotalCents ??
          quote.totalCents`, so once Helen has typed a total the ENGINE's status
          no longer describes the figure on screen. `wasHandQuoted` is set for
          any finish({finalTotalCents}) — Override on a live estimate as well as
          Record agreed — so without the gate an 'estimated' engine quote Helen
          OVERRODE rendered "Agreed priceEstimate" on a number she typed
          herself: the exact inverse of the mislabelling this milestone fixes. */}
      {total != null && !parked && (
        <div class="quote-total-row">
          <span class="quote-total-label">
            {job.wasHandQuoted ? 'Agreed price' : 'Total'}
            {!job.wasHandQuoted && job.quote?.status === 'estimated' &&
              <span class="chip chip--estimate">Estimate</span>}
          </span>
          <span class="quote-total">{euro(total)}</span>
        </div>
      )}

      {/* ⭐⭐ T1749 M8 (the fold, measured on the seat) — THE ATTRIBUTION AND THE
          CONTACT ARE ONE LINE. They were two stacked paragraphs, `Quoted by
          Helen` and the number, 30px + 44px plus their margins for two short
          facts that are read together — and this screen's gold primary measured
          775 against the ~700 plane at 1024x700 (`e2e/tests/green/tablet-ia.spec.ts`).
          Merging them is the cheapest of the cuts and the one that removes a LINE
          rather than a gap: -34px, with no fact lost and nothing said twice.
          ⚠️ THE GATE IS `saved || phone`, NOT the phone alone: the attribution
          half is true of every outcome (a price check is still quoted by
          somebody), so the line renders with or without a number and the number
          joins it when there is one.
          ⚠️ THE CLASS PAIR IS DELIBERATE. It is both things now, and
          `.done-contact` is what carries the link colour and what four specs and
          the `a[href^="tel:"]` census read. */}
      <p class="done-attribution done-contact">
        {parked ? 'Saved by' : 'Quoted by'} {props.operator}
        {job.customerPhone && ' · '}
        {/* Gated on a phone having been TYPED: with no number in play there is
            nothing to show. (Board 2814 M6 removed the account-booking branch
            here, with the nursing tile; see the note above `sendBlock`.) */}
        {job.customerPhone && (
          /* ⭐ BOARD 2089 M3 (R-7) — the SAME read-back projection
             `CustomerInfoBlock` uses, handed this screen's own extra fact: on a
             saved walk-in the server has already parsed and stored the number, so
             `serverPhone` is the truer answer than the string Helen typed, and
             `serverPhoneKnown` is what says the read has landed. Unknown, or a
             number that refuses, and both halves stay exactly as they were.
             ⚠️ NO REFUSAL LINE HERE, deliberately: Done is the outcome screen and
             the send block below it already states, in its own words, whether a
             message can go. Two sentences about one condition on one screen is
             what this milestone is removing elsewhere.
             ⛔ BOARD 2089 M5 — `href === null` IS THE RECORD SAYING NO NUMBER, and
             it renders as TEXT. A link here would be T651 again on a walk-in: a
             dial action from what Helen typed, above the send block's refusal
             from what the server stored. */
          donePhone.href === null
            ? donePhone.text
            : <a href={`tel:${donePhone.href}`}>{donePhone.text}</a>
        )}
      </p>

      {!saved && !parked && (
        <Banner kind="info">
          Nothing was saved. Add the customer's name next time to keep a record.
        </Banner>
      )}

      {/* ⭐ board 2812 M5 (fix round 1, W7): the sentence says WHERE the saved quote is.
          A park keeps the booking `new` (core/park-booking.ts), so it is in no To do pile
          (`listPileCandidates` reads `quoted` only); a quote started from a web enquiry is
          that enquiry's card, quoting, on Web enquiries' To do (Inbox.tsx's `quoting`,
          "Resume quote"); a walk-in has no enquiry, so it is found in Bookings. */}
      {parked && (
        <Banner kind="ok">
          {job.inquiryId != null ? PARKED_WORDS.fromEnquiry : PARKED_WORDS.walkIn}
        </Banner>
      )}

      {depositPaid && <span class="chip chip--paid">Deposit paid</span>}

      {saved && job.depositSent && job.depositAmountCents != null && !depositPaid && (
        <Banner kind="ok">
          Deposit sent: {euro(job.depositAmountCents)}.
          {job.depositUrl && (
            <>
              {' '}
              <button
                type="button"
                class="btn btn--quiet btn--inline"
                onClick={() => {
                  const url = job.depositUrl;
                  if (!url) return;
                  void copyHttps(url).then((ok) => setCopied(ok ? 'copied' : 'failed'));
                }}
              >
                {copied === 'copied' ? 'Link copied' : copied === 'failed' ? 'Could not copy' : 'Copy payment link'}
              </button>
              {' '}
              <button type="button" class="btn btn--quiet btn--inline" onClick={() => job.depositUrl && openHttps(job.depositUrl)}>
                Open payment page
              </button>
            </>
          )}
        </Banner>
      )}

      {/* D17c: the customer send — its own section, explicit recipient. */}
      {sendBlock && (
        <SendQuote
          // Per-send channel override = component state, so the booking id is
          // its identity: a different booking must never inherit it (see the
          // same key on BookingDetail).
          key={job.bookingId}
          serverPhone={job.serverPhone}
          serverPhoneKnown={job.serverPhoneKnown}
          serverEmail={job.customerEmail}
          // T549 M8 - the provenance bit refreshBookingDetail resolved beside
          // the address. Done is the highest-traffic quoting surface, so it
          // gets the same signal the booking-detail panel gives: an address a
          // stranger could have typed must not read as the business's own
          // record on the screen Helen quotes from.
          serverEmailFromInquiry={job.customerEmailFromInquiry}
          contactPreference={job.contactPreference}
          // T660 M4 (plan step 4) - the failed-detail-read state, which only
          // this host can reach. `refreshBookingDetail` has no retry and nothing
          // re-runs it, so without this the block promised a load that was never
          // coming. BookingDetail passes nothing: it renders its own error
          // screen instead of the booking, so it can never mount this block in
          // that state.
          contactReadFailed={job.serverDetailFailed}
          latestSend={job.latestSend}
          sendStatusLabel={props.sendStatusLabel}
          channelLabel={props.channelLabel}
          channelConfigured={props.channelConfigured}
          onSend={async (includeDeposit, channel) => {
            // The witness: the outcome is the host's to see (see `sentOk` above),
            // and it is returned unchanged so the block's own states are untouched.
            const res = await props.onSendQuote(includeDeposit, channel);
            if (res.ok) setSentOk(true);
            return res;
          }}
          // T660 M2 - the manual copy route, inside the shared block. The
          // in-flight `quotePending` guard stays wrapped around this handler in
          // App.tsx (Done holds no store), which is why a HANDLER travels here
          // and not a gate expression: BookingDetail's equivalent needs no such
          // guard and must not inherit one.
          //
          // `?? null` is the whole gate that is left. Every other conjunct the
          // old call site spelled out (`saved`, a bookingId, a positive total)
          // is the condition this block is mounted on, so restating it here
          // would be a second opinion about the same question.
          onCopyMessage={props.onCopyQuoteMessage ?? null}
          // T660 M3 - the payment-link copy row, same `?? null` gate and the
          // same reasoning as the line above.
          onCopyPaymentLink={props.onCopyPaymentLink ?? null}
        />
      )}

      {/* ⭐⭐ T1749 M8 round 4 (impeccable P3) — IT SITS BELOW THE SEND BLOCK NOW, and
          the move is a FOLD fix on a common state. A second call from a number
          already on file renders this notice, and above the block it cost 128px:
          measured at 1024x700 the screen's gold primary bottomed at 825.7, which
          is the heaviest state Done has.
          ⛔ IT IS INFORMATIONAL, NOT A DECISION. Nothing about it changes what
          Helen does next — the send is the next act either way — and §7.3 puts a
          decision above the control it governs, not a note.
          ⚠️⚠️ AND IT IS BELOW THE ~700 PLANE WHERE IT SITS: measured live at
          1024x700 the notice runs y 801.3 to 917.3, so on a 700-tall tablet it is
          wholly off screen and reached by SCROLLING. The earlier wording here ("it is still on the
          screen") was wrong and is deleted. What holds instead, and what the trade
          was: `Banner kind="info"` carries `role="status"`, so it IS ANNOUNCED on
          render whether or not it is in the viewport, and the two facts cannot
          both be inside the plane — above the send block the notice was in view
          and the gold primary bottomed at 825.7 (the heaviest state Done has),
          which is the worse failure of the two. Recorded as bug-i in
          `e2e/KNOWN-BUGS.md` with both measurements; board #1749's call.
          ⚠️ IT IS NOT A ONE-LINER: the sentence names what happened AND what to
          check ("Check it is the right customer before sending"), and shortening
          it would drop the instruction — the half P0-12 exists for. */}
      {/* T660 M5 / P0-12 — the re-attach the server could NOT refuse. It refuses
          before writing anything when the typed address and the typed phone
          resolve to two different customers; where there was no phone (or none
          on file), it cannot tell "returning customer, new mobile" from "typo
          that happens to be somebody else's address", so it attaches and says
          so. Stating it here is what keeps the re-attach from being silent,
          which is the half of P0-12 the refusal does not cover.

          ⛔ IT NAMES NOBODY: no address, no name, no number. The matched
          customer's details are not on this screen and must not be put here —
          echoing them back would make the capture field an enumeration oracle
          (SEC-5). The CONDITION is disclosed; the PARTY is not. */}
      {saved && job.customerMatchedBy === 'email' && (
        <Banner kind="info">
          This was added to a customer already on file for that email address, not a new one. Check it
          is the right customer before sending.
        </Banner>
      )}

      {suggestable != null && (suggested === 'idle' || suggested === 'sending') && (
        <p class="done-suggest">
          <button type="button" class="btn btn--quiet" disabled={suggested === 'sending'} onClick={() => void sendSuggestion()}>
            Suggest for the price list
          </button>
        </p>
      )}
      {/* ⭐ DS-14's FIRST CONSUMER (T1749 M10). `reveal` is a BOOLEAN and nothing
          else: the enter class, the frame flip and the suppression all live in
          `Notice` / `@ecmb/ui/styles/components.css`, so this screen owns no
          animation code and every later adopter gets the same reveal for free.
          ⛔ THE CLASS NAME IS DELIBERATELY NOT WRITTEN HERE. `banner--` is an armed
          `legacy-selectors` entry scoped to the apps — a `banner--reveal` literal in
          this file would red that gate, and correctly: the library composes it. */}
      {suggested === 'sent' && (
        <Banner kind="ok" reveal>Suggested. It lands in the review queue on the rates screen.</Banner>
      )}
      {suggested === 'failed' && (
        <Banner kind="error">
          Suggesting it didn't work.{' '}
          <button type="button" class="btn btn--quiet btn--inline" onClick={() => void sendSuggestion()}>
            Try again
          </button>
        </Banner>
      )}

      <hr class="section-rule" />
      <div class="action-row">
        {/* ⭐ T1749 M8 (DS-20 d) — the way back to the pending work. QUIET, and
            beside the exit rather than above it: it is an alternative to
            'New Quote', not a second thing to do. It is neither a substring nor
            a superstring of any locked name on this screen ('Back' is the
            shell's own control and lives outside this pane; Playwright matches
            an accessible name by case-insensitive SUBSTRING, so 'Back to
            Bookings' is queried whole and the shell's 'Back' is queried exact -
            see the e2e net). Withheld when no handler is wired, so a Done
            rendered without one shows no control rather than a dead one. */}
        {props.onBackToBookings && (
          <button type="button" class="btn btn--quiet" onClick={props.onBackToBookings}>
            Back to Bookings
          </button>
        )}
        {/* T660 M6 (P0-22, corrected in R1): SECONDARY exactly when a primary
            SEND control is on screen, primary otherwise. Gold is the screen's
            one "do this next" signal, and on a saved quote with a reachable
            customer the thing to do next is SEND it - that button (SendQuote,
            above) holds the primary and 'New Quote' must not compete, because
            dressing the exit as the primary action pointed Helen away from the
            send. But P0-22's first pass made this unconditional, which left a
            parked save, a price check, an already-sent quote and a quoted
            booking with no contact details showing NO primary at all - a screen
            with nothing dominant is not a quieter screen, it is a directionless
            one. Conditional, in the shape M4 already used on this surface (the
            send button's own `latestSend ? quiet : primary`). Label unchanged
            either way: it is matched exactly in both harnesses. */}
        <button
          type="button"
          class={`btn ${primaryIsSend ? 'btn--secondary' : 'btn--primary'}`}
          onClick={props.onNewQuote}
        >
          New Quote
        </button>
      </div>
    </div>
  );
}
